import { useToggleShopNewPortal } from "@/api/super-admin/use-super-admin-shop-portal";
import { Switch } from "@babackapp/ui/components/switch";
import { Link } from "react-router-dom";
import { CustomerPortalTestOrder } from "./customer-portal-test-order";
import type { PortalShop } from "./customer-portal-types";

function BoolBadge({ value, label }: { value: boolean; label: string }) {
  return (
    <span
      title={label}
      className={[
        "inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold",
        value ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-400",
      ].join(" ")}
    >
      {value ? "✓" : "–"}
    </span>
  );
}

/** Toggles the shop's V3 portal opt-in, then refetches the super admin list. */
function NewPortalSwitch({
  shopId,
  isEnabled,
}: {
  shopId: string;
  isEnabled: boolean;
}) {
  const { mutate, isPending } = useToggleShopNewPortal(shopId);

  return (
    <div className="flex items-center gap-2">
      <Switch
        checked={isEnabled}
        disabled={isPending}
        onCheckedChange={(next) => mutate(next)}
        aria-label="Nouveau portail client"
      />
      <span
        className={[
          "text-[10px] font-semibold uppercase tracking-wide",
          isEnabled ? "text-violet-600" : "text-muted-foreground",
        ].join(" ")}
      >
        {isEnabled ? "Nouveau" : "Ancien"}
      </span>
    </div>
  );
}

/** Last return request, in months when it is old enough for that to be what matters. */
function LastActivity({ ts }: { ts: number | null }) {
  if (!ts) {
    return (
      <span
        title="Aucun retour enregistré"
        className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500"
      >
        aucun retour
      </span>
    );
  }

  const days = Math.round((Date.now() - ts) / 86_400_000);
  const label = days >= 60 ? `${Math.round(days / 30)} mois` : `${days} j`;

  return (
    <span
      title={`Dernier retour : ${new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(ts))}`}
      className={[
        "rounded px-1.5 py-0.5 text-[10px] font-medium",
        days >= 180
          ? "bg-red-50 text-red-600"
          : days >= 90
            ? "bg-amber-50 text-amber-700"
            : "bg-emerald-50 text-emerald-700",
      ].join(" ")}
    >
      {label}
    </span>
  );
}

export function CustomerPortalRow({ shop }: { shop: PortalShop }) {
  return (
    <tr className="border-b last:border-0 hover:bg-muted/30">
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <Link
            to={`/shops/${shop.id}`}
            className="font-medium hover:text-violet-600 hover:underline"
          >
            {shop.name}
          </Link>
          <LastActivity ts={shop.lastActivityAt ?? null} />
        </div>
        <div className="text-xs text-muted-foreground">{shop.slug}</div>
      </td>
      <td className="px-3 py-2">
        <CustomerPortalTestOrder
          shopId={shop.id}
          shopifyUrl={shop.shopifyUrl}
          providerId={shop.providerId}
          storefrontUrl={shop.storefrontUrl ?? null}
        />
      </td>
      <td className="px-3 py-2">
        <NewPortalSwitch
          shopId={shop.id}
          isEnabled={shop.isNewCustomerPortalEnabled ?? false}
        />
      </td>
      <td className="px-3 py-2 text-center">
        <BoolBadge value={shop.hasExchanges ?? false} label="Échanges" />
      </td>
      <td className="px-3 py-2 text-center">
        <BoolBadge
          value={shop.hasExchangeCredit ?? false}
          label="Échanges crédit"
        />
      </td>
      <td className="px-3 py-2 text-center">
        <BoolBadge value={shop.hasGiftCode ?? false} label="Avoirs" />
      </td>
      <td className="px-3 py-2 text-center">
        <BoolBadge
          value={shop.hasCustomColors ?? false}
          label="Couleurs perso"
        />
      </td>
      <td className="px-3 py-2 text-center">
        <BoolBadge value={shop.hasCustomCss ?? false} label="CSS perso" />
      </td>
      <td className="px-3 py-2 text-center">
        <BoolBadge value={shop.isActive ?? true} label="Actif" />
      </td>
    </tr>
  );
}
